import React, { createContext, useContext, useState, useEffect } from "react";
import { authAPI } from "../services/api";

export interface AuthUser {
  nombre: string;
  apellido: string;
  rol: string;
  email: string;
  academia_id: string | null;
}

interface AuthContextType {
  user: AuthUser | null;
  token: string | null;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  isAuthenticated: boolean;
  isAdmin: () => boolean;
  isReadOnly: () => boolean;
  hasRole: (...roles: string[]) => boolean;
  refreshMe: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({} as AuthContextType);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [token, setToken] = useState<string | null>(localStorage.getItem("token"));

  useEffect(() => {
    const stored = localStorage.getItem("user");
    if (stored) { try { setUser(JSON.parse(stored)); } catch {} }
  }, []);

  const login = async (email: string, password: string) => {
    const res = await authAPI.login(email, password);
    const { access_token, rol, nombre, apellido, academia_id } = res.data;
    localStorage.setItem("token", access_token);
    const u: AuthUser = { nombre, apellido: apellido || "", rol, email, academia_id: academia_id || null };
    localStorage.setItem("user", JSON.stringify(u));
    setToken(access_token);
    setUser(u);
  };

  const logout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    setToken(null);
    setUser(null);
  };

  const refreshMe = async () => {
    try {
      const res = await authAPI.me();
      const updated: AuthUser = {
        nombre: res.data.nombre,
        apellido: res.data.apellido || "",
        rol: res.data.rol,
        email: res.data.email,
        academia_id: res.data.academia_id || null,
      };
      localStorage.setItem("user", JSON.stringify(updated));
      setUser(updated);
    } catch {}
  };

  const isAdmin = () => (user?.rol || "").toLowerCase() === "admin";
  const isReadOnly = () => ["jugador", "padre"].includes((user?.rol || "").toLowerCase());
  const hasRole = (...roles: string[]) => roles.map(r => r.toLowerCase()).includes((user?.rol || "").toLowerCase());

  return (
    <AuthContext.Provider value={{ user, token, login, logout, isAuthenticated: !!token, isAdmin, isReadOnly, hasRole, refreshMe }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
